function uniqueIds(ids) {
  const seen = new Set();
  const result = [];
  for (const raw of ids || []) {
    const id = Number(raw);
    if (Number.isNaN(id) || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}

function asIdSet(ids) {
  if (!ids) return new Set();
  if (ids instanceof Set) return new Set([...ids].map(Number));
  return new Set(uniqueIds(ids));
}

/**
 * Conserva a ordem relativa já salva.
 * Quem continua entra na ordem nova da lista.
 * Quem está no mapa e não está na lista permanece no lugar, se ainda puder ficar.
 * Quem nunca foi memorizado entra no fim.
 */
export function mergeNeighborhoodOrder(
  savedIds,
  currentIdsInOrder,
  keepAbsentIds
) {
  const current = uniqueIds(currentIdsInOrder);
  const currentSet = new Set(current);
  const keep = asIdSet(keepAbsentIds);
  const saved = uniqueIds(savedIds);
  const savedSet = new Set(saved);

  const spine = saved.filter((id) => currentSet.has(id) || keep.has(id));
  const knownQueue = current.filter((id) => savedSet.has(id));

  const result = [];
  let knownIndex = 0;
  for (const id of spine) {
    if (currentSet.has(id)) {
      result.push(knownQueue[knownIndex]);
      knownIndex += 1;
    } else {
      result.push(id);
    }
  }

  for (const id of current) {
    if (!savedSet.has(id)) result.push(id);
  }

  return result;
}

/**
 * Pulos já gravados (com ordem e fora da lista de hoje) ficam no topo.
 * O cliente pulado agora entra logo depois deles.
 * Visita sem ordem permanece na posição salva.
 */
export function orderIdsAfterJump({
  savedIds,
  todayIds,
  jumpedClientId,
  jumpIds,
}) {
  const saved = uniqueIds(savedIds);
  const today = asIdSet(todayIds);
  const jumps = asIdSet(jumpIds);
  const jumped = Number(jumpedClientId);

  const pending = saved.filter(
    (id) => id !== jumped && !today.has(id) && jumps.has(id)
  );
  const pendingSet = new Set(pending);
  const rest = saved.filter((id) => id !== jumped && !pendingSet.has(id));
  const savedSet = new Set(saved);
  const newcomers = [...today].filter((id) => id !== jumped && !savedSet.has(id));

  return uniqueIds([...pending, jumped, ...rest, ...newcomers]);
}

export function savedIdsForNeighborhood(previousMap, bairroId) {
  if (!previousMap) return [];
  const clientsMap =
    previousMap[bairroId] || previousMap[String(bairroId)] || null;
  if (!clientsMap || typeof clientsMap !== "object") return [];

  return Object.entries(clientsMap)
    .map(([id, pos]) => ({ id: Number(id), pos: Number(pos) }))
    .filter((entry) => !Number.isNaN(entry.id))
    .sort((a, b) => a.pos - b.pos)
    .map((entry) => entry.id);
}

export function buildDailyMapOrder({
  previousMap,
  neighborhoods,
  dailyList,
  keepAbsentIds,
}) {
  const fullOrder = [];
  const seen = new Set();
  const listedNeighborhoods = neighborhoods || [];
  const neighborhoodIds = new Set(
    listedNeighborhoods.map((neighborhood) => String(neighborhood.id))
  );

  const appendIds = (bairroId, ids) => {
    for (const id of ids) {
      if (seen.has(id)) continue;
      fullOrder.push({ id, BairroId: Number(bairroId) });
      seen.add(id);
    }
  };

  const mergeNeighborhood = (bairroId) => {
    const savedIds = savedIdsForNeighborhood(previousMap, bairroId);
    const currentIds = (dailyList || [])
      .filter((client) => String(client.BairroId) === String(bairroId))
      .map((client) => client.id);
    appendIds(
      bairroId,
      mergeNeighborhoodOrder(savedIds, currentIds, keepAbsentIds)
    );
  };

  for (const neighborhood of listedNeighborhoods) {
    mergeNeighborhood(neighborhood.id);
  }

  for (const bairroId of Object.keys(previousMap || {})) {
    if (neighborhoodIds.has(String(bairroId))) continue;
    mergeNeighborhood(bairroId);
  }

  const dailyMap = {};
  fullOrder.forEach((client, index) => {
    if (!dailyMap[client.BairroId]) dailyMap[client.BairroId] = {};
    dailyMap[client.BairroId][client.id] = index + 1;
  });
  return dailyMap;
}
